// src/components/UpdatePrompt.jsx

import { useRegisterSW } from 'virtual:pwa-register/react';
import UpdateBanner from '@prism-ehr/shared/components/ui/UpdateBanner';
import { getEnvironmentConfig } from '@/config/platforms';

const UPDATE_CHECK_INTERVAL_MS = 5 * 60 * 1000;

export function UpdatePrompt() {
  const {
    needRefresh: [needRefresh],
    updateServiceWorker
  } = useRegisterSW({
    onRegisteredSW(_swUrl, registration) {
      if (!registration) return;

      // Browsers only check for a new SW on hard navigation (~24h otherwise),
      // so poll explicitly and re-check whenever the tab regains visibility.
      const checkForUpdate = () => {
        // update() rejects when offline — swallow and retry on next tick
        registration.update().catch(() => {});
      };

      setInterval(checkForUpdate, UPDATE_CHECK_INTERVAL_MS);
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          checkForUpdate();
        }
      });
    }
  });

  const handleReload = async () => {
    const registration = 'serviceWorker' in navigator
      ? await navigator.serviceWorker.getRegistration()
      : null;

    if (registration?.waiting) {
      // vite-plugin-pwa 0.19.x only auto-reloads when the new SW counts as an
      // "update" of the one it registered; workers discovered via our manual
      // registration.update() polling are classified "external" and skip that
      // reload. Reload ourselves once the new worker takes control.
      navigator.serviceWorker.addEventListener(
        'controllerchange',
        () => window.location.reload(),
        { once: true }
      );
      await updateServiceWorker(true);
    } else {
      // New worker already took control (or no SW) — a plain reload is served
      // by the new worker's precache
      window.location.reload();
    }
  };

  return (
    <UpdateBanner
      visible={needRefresh}
      appName={getEnvironmentConfig().appName}
      onReload={handleReload}
    />
  );
}
